'use client'

import {
  useEffect,
  useMemo,
  useState,
} from 'react'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import {
  Bell,
  Menu,
} from 'lucide-react'

import type { AppShellProps } from '@/components/layout/AppShell.types'
import { Button } from '@/components/ui/button'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { MessagesHeaderButton } from '@/components/inbox/MessagesHeaderButton'
import { InviteFriendsButton } from '@/components/common/InviteFriendsButton'
import { HeaderUserDropdown } from '@/components/header/HeaderUserDropdown'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import PromanitySystemClock from '@/components/header/SystemClock'
import { BusinessMembershipExpiryAlert } from '@/components/alerts/BusinessMembershipExpiryAlert'
import { BusinessMembershipPaymentModal } from '@/components/alerts/BusinessMembershipPaymentModal'
import { EmailQuotaUsageAlert } from '@/components/alerts/EmailQuotaUsageAlert'
import { OnboardingProgressBanner } from '@/components/dashboard/onboarding/OnboardingProgressBanner'
import { useChannel } from '@/contexts/ChannelContext'
import { useFunnelBuilderMode } from '@/contexts/FunnelBuilderModeContext'
import { runCommunityDemo } from '@/features/community/demo/communityDemoEngine'
import { cn } from '@/lib/utils'

const ConfigSidebar = dynamic(
  () =>
    import('@/components/ConfigSidebar').then(
      (module) => module.ConfigSidebar
    ),
  { ssr: false }
)

const ContextNav = dynamic(
  () =>
    import('@/components/MainNav').then(
      (module) => module.ContextNav
    ),
  { ssr: false }
)

const RoomsChat = dynamic(
  () =>
    import('@/components/rooms/RoomsChat').then(
      (module) => module.RoomsChat
    ),
  { ssr: false }
)

export function HubeeShell({
  children,
  gate,
  enableGlassUI,
  backgroundImage,
  loggedIn,
  userId,
  features,
  isAffiliateActive,
  inviteUrl,
  isPromanityDashboard,
  sellerId,
  slots,
  immersive,
}: AppShellProps) {
  const pathname = usePathname() || ''

  const isLaunchpad = pathname === '/launchpad'
  const isCommunity = pathname === '/community'
  const isFunnelRoute =
    pathname === '/funnel-builder' ||
    pathname.startsWith('/funnel-builder/')

  const { selectedChannel, selectedChannelName } =
    useChannel()

  const { builderMode } = useFunnelBuilderMode()

  const isImmersive = immersive === true

  const [samanthaDockOpen, setSamanthaDockOpen] =
    useState(false)

  const samanthaHiddenPrefixes = [
    '/f',
    '/funnel-view',
    '/funnel-preview',
    '/inbox',
    '/login',
    '/product',
    '/start',
    '/activate',
    '/awaken',
    '/settings',
  ]

  const isSamanthaHidden =
    !pathname ||
    pathname === '/' ||
    samanthaHiddenPrefixes.some(
      (prefix) =>
        pathname === prefix ||
        pathname.startsWith(`${prefix}/`)
    )

  const hasSamanthaWidget = !isSamanthaHidden

  useEffect(() => {
    function handleSamanthaDockVisibility(event: Event) {
      const customEvent =
        event as CustomEvent<{ open?: boolean }>

      setSamanthaDockOpen(
        Boolean(customEvent.detail?.open)
      )
    }

    window.addEventListener(
      'samantha:dock-visibility-change',
      handleSamanthaDockVisibility
    )

    return () => {
      window.removeEventListener(
        'samantha:dock-visibility-change',
        handleSamanthaDockVisibility
      )
    }
  }, [])

  const activeCategoryId = useMemo(() => {
    if (
      !selectedChannel ||
      selectedChannel === 'general'
    ) {
      return 0
    }

    const categoryId = Number(selectedChannel)

    return Number.isFinite(categoryId)
      ? categoryId
      : 0
  }, [selectedChannel])

  const activeChannelName = useMemo(() => {
    if (
      !selectedChannel ||
      selectedChannel === 'general'
    ) {
      return 'Todos'
    }

    return selectedChannelName || 'Canal'
  }, [selectedChannel, selectedChannelName])

  const shouldRenderRooms =
    isCommunity &&
    Boolean(sellerId) &&
    activeCategoryId > 0

  const shouldShowFloatingOnboarding =
    gate.isOwner &&
    Boolean(sellerId) &&
    !isFunnelRoute

  if (isImmersive) {
    return (
      <div
        className={cn(
          'relative h-dvh w-dvw overflow-hidden bg-black text-foreground',
          enableGlassUI && 'glass-mode'
        )}
      >
        {slots?.background}

        <main className="relative z-10 h-full w-full overflow-hidden">
          {children}
        </main>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'relative flex h-screen w-full overflow-hidden bg-background text-foreground',
        enableGlassUI && 'glass-mode'
      )}
      style={
        backgroundImage
          ? {
              backgroundImage: `linear-gradient(180deg, hsl(var(--background) / 0.32), hsl(var(--background) / 0.66)), url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : undefined
      }
    >
      {slots?.background}

      <div className="pointer-events-none absolute inset-0 hidden overflow-hidden md:block">
        <div className="absolute -left-24 top-24 h-[360px] w-[360px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-20 top-32 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-[-160px] left-[18%] h-[360px] w-[520px] rounded-full bg-primary/5 blur-3xl" />
      </div>

      <ConfigSidebar
        enableGlassUI={enableGlassUI}
      />

      {loggedIn && isPromanityDashboard && (
        <>
          <PromanitySystemClock />

          <button
            type="button"
            className="rounded-xl border border-border bg-background/60 p-2 text-muted-foreground transition hover:border-cyan-500/20 hover:bg-cyan-500/10 hover:text-cyan-600 dark:hover:text-cyan-400"
          >
            <Bell size={18} />
          </button>
        </>
      )}

      {slots?.header ??
        (loggedIn ? (
          <div
            data-app-header
            data-hubee-chrome="true"
            className="pointer-events-auto fixed right-7 top-6 z-10 hidden items-center gap-2 md:z-[50] md:flex"
          >
            <button
              className="hidden"
              type="button"
              onClick={() => {
                if (
                  window.location.pathname ===
                  '/community'
                ) {
                  runCommunityDemo()
                }
              }}
            >
              ?
            </button>

            <NotificationBell />

            <MessagesHeaderButton userId={userId} />

            {features?.affiliate_invites &&
              isAffiliateActive &&
              inviteUrl && (
                <InviteFriendsButton
                  url={inviteUrl}
                />
              )}

            <HeaderUserDropdown />
          </div>
        ) : null)}

      <div
        className={cn(
          'relative z-10 flex min-w-0 flex-1 flex-col overflow-hidden md:pr-6 md:pb-0',
          'md:pl-7',
          'transition-[opacity,filter,transform] duration-200 ease-out',
          samanthaDockOpen &&
            !isLaunchpad &&
            'pointer-events-none scale-[0.995] opacity-45 blur-[1px]',
          isLaunchpad
            ? 'md:pt-6'
            : hasSamanthaWidget
              ? 'md:pt-[116px]'
              : 'md:pt-[60px]'
        )}
      >
        {slots?.mobileHeader ?? (
          <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:hidden">
            <SidebarTrigger>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SidebarTrigger>

            <div className="hidden min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">
                Hubee
              </div>
            </div>

            {loggedIn ? (
              <>
                <NotificationBell />

                <MessagesHeaderButton
                  userId={userId}
                />

                {features?.affiliate_invites &&
                  isAffiliateActive &&
                  inviteUrl && (
                    <InviteFriendsButton
                      url={inviteUrl}
                    />
                  )}

                <HeaderUserDropdown />
              </>
            ) : null}
          </header>
        )}

        {isCommunity ? (
          <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
            <div className="mx-auto h-full min-h-0 w-full max-w-[1512px]">
              <div className="grid h-full min-h-0 grid-cols-1 gap-5 overflow-visible p-0 md:p-3 xl:grid-cols-[300px_minmax(0,780px)_400px]">
                {slots?.sidebar ?? <ContextNav />}

                <main className="min-h-0 min-w-0 overflow-visible">
                  {gate.isOwner && (
                    <>
                      <BusinessMembershipExpiryAlert
                        sellerMembershipActive={
                          gate.access
                            ?.sellerMembershipActive
                        }
                        sellerMembershipExpiry={
                          gate.access
                            ?.sellerMembershipExpiry
                        }
                        paymentUrl={
                          gate.access?.paymentUrl ||
                          '/setup?reason=membership_expiring'
                        }
                        hasStripeSubscription={
                          gate.access
                            ?.hasStripeSubscription
                        }
                        stripeSubscriptionStatus={
                          gate.access
                            ?.stripeSubscriptionStatus
                        }
                        stripeCurrentPeriodEnd={
                          gate.access
                            ?.stripeCurrentPeriodEnd
                        }
                      />

                      <EmailQuotaUsageAlert />
                    </>
                  )}

                  {gate.isOwner && (
                    <BusinessMembershipPaymentModal />
                  )}

                  {children}
                </main>

                {shouldRenderRooms ? (
                  <div
                    data-tour="community-chat-panel"
                    className="hidden min-h-0 w-full shrink-0 rounded-[2rem] hubee-premium-card xl:block"
                  >
                    <div className="hubee-premium-content h-full min-h-0 overflow-hidden">
                      <RoomsChat
                        sellerId={Number(sellerId)}
                        categoryId={
                          activeCategoryId
                        }
                        channelName={
                          activeChannelName
                        }
                        className="h-full border-0 bg-transparent shadow-none"
                        tourMode
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden md:flex-row md:gap-5">
            {slots?.sidebar ?? <ContextNav />}

            <main
              className={cn(
                'min-h-0 min-w-0 flex-1 overflow-y-auto',
                isFunnelRoute &&
                  builderMode === 'editor' &&
                  'md:overflow-hidden'
              )}
            >
              {gate.isOwner && (
                <>
                  <BusinessMembershipExpiryAlert
                    sellerMembershipActive={
                      gate.access
                        ?.sellerMembershipActive
                    }
                    sellerMembershipExpiry={
                      gate.access
                        ?.sellerMembershipExpiry
                    }
                    paymentUrl={
                      gate.access?.paymentUrl ||
                      '/setup?reason=membership_expiring'
                    }
                    hasStripeSubscription={
                      gate.access
                        ?.hasStripeSubscription
                    }
                    stripeSubscriptionStatus={
                      gate.access
                        ?.stripeSubscriptionStatus
                    }
                    stripeCurrentPeriodEnd={
                      gate.access
                        ?.stripeCurrentPeriodEnd
                    }
                  />

                  <EmailQuotaUsageAlert />
                </>
              )}

              {gate.isOwner && (
                <BusinessMembershipPaymentModal />
              )}

              {children}
            </main>
          </div>
        )}
      </div>

      {shouldShowFloatingOnboarding ? (
        <>
          <div className="pointer-events-none fixed bottom-6 left-[112px] z-[65] hidden md:block">
            <OnboardingProgressBanner
              sellerId={Number(sellerId || 0)}
              variant="floating"
              className="pointer-events-auto w-[360px]"
            />
          </div>

          <div className="pointer-events-none fixed bottom-4 left-4 right-4 z-[65] md:hidden">
            <OnboardingProgressBanner
              sellerId={Number(sellerId || 0)}
              variant="floating"
              className="pointer-events-auto"
            />
          </div>
        </>
      ) : null}
    </div>
  )
}